import { SignatureByte, SotiAssistMessageType } from "app/modules/remote-control-ng/definitions";
import { MemoryBuffer } from "../../utils/memoryBuffer";
import { AttachmentActivity } from "app/modules/ticket/ticket.model";
import { BaseMessage } from "../baseMessage";
import { ISkinInformation } from "../../mcrc-api";

export interface ISacVideoRecordingOptions {
    recordMouseMovement?: boolean;
    recordMouseClicks?: boolean;
    includeDeviceSkin?: boolean;
}

/** SAC_VIDEO_RECORDING_START SotiAssistMessageType */
export class VideoRecordingStartMessage extends BaseMessage {
    private userConfig: ISacVideoRecordingOptions;

    constructor(options?: ISacVideoRecordingOptions) {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_RECORDING_START;
        this.userConfig = options;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        if (!!this.userConfig) {
            buffer.writeUInt8(this.userConfig.recordMouseMovement ? 1 : 0);
            buffer.writeUInt8(this.userConfig.recordMouseClicks ? 1 : 0);
            buffer.writeUInt8(this.userConfig.includeDeviceSkin ? 1 : 0);
        }
    }

    // No data to deserialize, just handle the command
}

/** SAC_VIDEO_RECORDING_CANCEL SotiAssistMessageType.
 *  When the VideoRecordingCancelMessage is sent as part of the closing the RC session
 *  the caller shouldn't wait for the cancel confirmation (identified by the reqId) before ending it */
export class VideoRecordingCancelMessage extends BaseMessage {
    constructor() {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_RECORDING_CANCEL;
    }

    // message has no custom body content, no serializeBody() override
}

/** SAC_VIDEO_RECORDING_PAUSE SotiAssistMessageType */
export class VideoRecordingPauseMessage extends BaseMessage {
    constructor() {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_RECORDING_PAUSE;
    }
}

/** SAC_VIDEO_RECORDING_STOP SotiAssistMessageType */
export class VideoRecordingStopMessage extends BaseMessage {
    constructor() {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_RECORDING_STOP;
    }

    // No data to deserialize, just handle the command
}

/** SAC_VIDEO_FILE_NAME SotiAssistMessageType */
export class VideoRecordingNameMessage extends BaseMessage {
    public fileName: string;

    constructor() {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_FILE_NAME;
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        this.fileName = buffer.readString();
    }
}

/** SAC_VIDEO_RECORDING_SETUP SotiAssistMessageType */
export class VideoRecordingSetupMessage extends BaseMessage {
    private device: ISkinInformation;

    constructor(device: ISkinInformation) {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_RECORDING_SETUP;
        this.device = device;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        if (this.device && this.device.id) {
            buffer.writeStringLE(this.device.hasNotchImage ? this.device.id + '.T' : this.device.id);

            buffer.writeUInt32BE(this.device.screen.top);
            buffer.writeUInt32BE(this.device.screen.left);
            buffer.writeUInt32BE(this.device.screen.bottom);
            buffer.writeUInt32BE(this.device.screen.right);

            buffer.writeUInt32BE(this.device.deviceWidth);
            buffer.writeUInt32BE(this.device.deviceHeight);

            buffer.writeUInt8(this.device.rotation);
        }
    }
}

/** SAC_CLIENT_SIDE_ROTATION SotiAssistMessageType */
export class VideoRotationMessage extends BaseMessage {
    private rotation: number;

    constructor(rotation: number) {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_CLIENT_SIDE_ROTATION;
        this.rotation = rotation;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        buffer.writeCompInt(this.rotation);
    }
}

/** SAC_VIDEO_RECORDING_DOWNLOAD_READY SotiAssistMessageType */
export class VideoRecordingDownloadReadyMessage extends BaseMessage {
    public draftActivity: AttachmentActivity;

    constructor() {
        super();
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_VIDEO_RECORDING_DOWNLOAD_READY;
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        let activity = <AttachmentActivity>{};
        activity.activityId = buffer.readInt32BE();
        activity.fileId = buffer.readString();
        activity.fileName = buffer.readString();
        this.draftActivity = activity;
    }
}

/** SAC_GET_VIDEO_DOWNLOAD_LINK SotiAssistMessageType */
export class VideoDownloadLinkMessage extends BaseMessage {
    private fileName?: string;
    private deviceId?: string;

    constructor(fileName?: string, deviceId?: string) {
        super();
        this.fileName = fileName;
        this.deviceId = deviceId;
        this.header.sig = SignatureByte.SignatureSotiAssistMessage;
        this.header.command = SotiAssistMessageType.SAC_GET_VIDEO_DOWNLOAD_LINK;
    }

    public serializeBody(buffer: MemoryBuffer): void {
        buffer.writeStringLE(this.fileName);
        buffer.writeStringLE(this.deviceId);
    }

    /** deprecated - does not work when RC channel gets disconnected
    *   Use "rcsessions/createDownload" remote control api call instead
    * */
    // public deserializeBody(buffer: MemoryBuffer): void {
    //     var downloadLink = buffer.readString();
    //     var fileName = buffer.readString();
    //     var requestId = this.header.requestId;
    //     //TODO: Still need to have the correct reqId as index
    //     SAC_CallBack[requestId](downloadLink);
    // }
}
